/* eslint-disable no-param-reassign */
import _ from "lodash";
import { useState } from "react";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import { callApi } from "../../api";
import { useAuth } from "../useAuth";
import UseEmployeeData from "./UseEmployeeData";

const LOADING_TYPES = {
  FETCH_TIMESHEET: "fetch_timesheet",
  UPDATE_SETTINGS: "update_settings",
  UPDATE_TIMESHEET: "update_timesheet",
  FETCH_ACTIVITY: "fetch_activity",
};

const SETTINGS_ID = "staff-timesheets";

const useStaffTimesheets = () => {
  const [timesheet, setTimesheet] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(null);
  const [activity, setActivity] = useState(null);

  const { selectedBusiness, selectedBusinessDetail } = useAuth();

  const { employeeData, getEmployeeData } = UseEmployeeData();

  const getBusinessSettingsForTimesheet = async () => {
    const res = await callApi(
      `/protected/business/${selectedBusiness.id}/tablesettings/${SETTINGS_ID}/get`
    );
    if (res && res.status === true) {
      setSettings(res.data ? res.data || null : null);
    } else {
      toast.error(
        (res && res.message) || "Failed to fetch business table settings!"
      );
    }
  };

  const updateBusinessSettingsForTimesheet = async (data) => {
    setLoading(LOADING_TYPES.UPDATE_SETTINGS);
    const _body = {
      settings: {
        title: "Staff Timesheets Settings",
        id: settings?.id || undefined,
        configuration: data,
      },
    };
    const res = await callApi(
      `/protected/business/${selectedBusiness.id}/tablesettings/${SETTINGS_ID}/update`,
      "POST",
      _body
    );
    if (res && res.status === true) {
      toast.success("Updated successfully");
      setSettings(res.data);
    } else {
      toast.error(res.message || "Failed to update!");
    }
    setLoading(null);
  };

  const getTimesheets = async (startDate, endDate) => {
    setLoading(LOADING_TYPES.FETCH_TIMESHEET);
    if (!employeeData) await getEmployeeData();
    if (!settings) await getBusinessSettingsForTimesheet();
    const res = await callApi(
      `/protected/business/${selectedBusiness.id}/timesheets?start=${startDate}&end=${endDate}`
    );
    if (res && res.status === true) {
      setTimesheet(res.data);
    } else {
      toast.error((res && res.message) || "Failed to fetch staff timesheets!");
    }
    setLoading(null);
  };

  const breakSettings =
    selectedBusinessDetail &&
    (selectedBusinessDetail.info?.payInfo
      ? selectedBusinessDetail.info.payInfo?.payrollHourSettings
      : selectedBusinessDetail.info?.payrollHourSettings);
  const breakMinutes = breakSettings?.breakMinutes;
  const breakHours = breakMinutes ? breakMinutes / 60 : 0;
  const breakThreshold = breakMinutes
    ? breakSettings?.breakThresholdHours
    : null;

  const dbFormat = "YYYY-MM-DD HH:mm:ss";
  const dayPretty = "ddd MMM DD, YYYY";
  const timeFormat = "hh:mm A";
  const dateTimePretty = "MMM Do, h:mm a";

  const clockInLimit = settings?.configuration?.clockInTime;
  const clockOutLimit = settings?.configuration?.clockOutTime;
  const longShiftLimit = settings?.configuration?.shiftDuration;

  const _24hrToMoment = (t) => moment(t, "HH:mm");

  const updateTimesheet = (ts, tz) => {
    const created = moment.utc(ts.clockin_at || ts.created_at, dbFormat).tz(tz);
    const updated = moment
      .utc(ts.clockout_at || ts.updated_at, dbFormat)
      .tz(tz);
    const origCreated = moment.utc(ts.original_clockin, dbFormat).tz(tz);
    const origUpdated = moment.utc(ts.original_clockout, dbFormat).tz(tz);
    const createdDay = created.format(dayPretty);
    const updatedDay = updated.format(dayPretty);
    const diffIn = origCreated.diff(created, "days");
    const diffOut = origUpdated.diff(updated, "days");
    const diffInHours = created.diff(origCreated, "hours");
    const diffOutHours = updated.diff(origUpdated, "hours");

    const clockOutAndClockInDiff = updated.diff(created, "days");

    ts.active = ts.status === "IN";
    ts.timestamp = created.valueOf();
    ts.abnormal = createdDay !== updatedDay;
    if (ts.active) {
      ts.hours = moment().diff(created, "minutes") / 60;
      ts.original_hours = ts.hours;
    }
    ts.day = createdDay;
    ts.clockedIn = created.format(timeFormat);
    ts.clockedOut = updated.format(timeFormat);
    ts.created = created.valueOf();
    ts.updated = updated.valueOf();

    ts.origClockedIn = origCreated.format(timeFormat);
    ts.origClockedOut = origUpdated.format(timeFormat);
    ts.clockedInFullDate = created.format(dateTimePretty);
    ts.clockedOutFullDate = updated.format(dateTimePretty);

    ts.origClockedInFullDate = origCreated.format(dateTimePretty);
    ts.origClockedOutFullDate = origUpdated.format(dateTimePretty);
    ts.dayDiffIn = diffIn;
    ts.dayDiffOut = diffOut;
    ts.isLateOut =
      clockOutAndClockInDiff > 0 ||
      (clockOutLimit &&
        _24hrToMoment(clockOutLimit).isBefore(
          _24hrToMoment(updated.format("HH:mm"))
        ));
    ts.isEarlyOut =
      !(clockOutAndClockInDiff > 0) &&
      clockOutLimit &&
      _24hrToMoment(clockOutLimit).isAfter(
        _24hrToMoment(updated.format("HH:mm"))
      );
    ts.isLateIn =
      clockInLimit &&
      _24hrToMoment(clockInLimit).isBefore(
        _24hrToMoment(created.format("HH:mm"))
      );
    ts.isEarlyIn =
      clockInLimit &&
      _24hrToMoment(clockInLimit).isAfter(
        _24hrToMoment(created.format("HH:mm"))
      );
    ts.isLongShift =
      ts.hours &&
      longShiftLimit &&
      parseFloat(ts.hours) > parseFloat(longShiftLimit);
    ts.diffShiftHours = ts.original_hours
      ? parseFloat(ts.hours) - parseFloat(ts.original_hours || 0)
      : null;
    ts.diffInHours = diffInHours;
    ts.diffOutHours = diffOutHours;
  };

  const getTimesheetSlot = (employees, timesheets, startDay, startTs) => {
    const thisDay = startDay.format("ddd MMM DD, YYYY");
    const daySlots = _.map(
      _.filter(timesheets, {
        day: thisDay,
      }),
      (obj) => ({
        ...obj,
        hours: +obj.hours,
        original_hours: +obj.original_hours,
        payroll_hours: +obj.payroll_hours,
      })
    );
    const gotSlots = [];
    const exportSlots = [];
    _.each(employees, (emp) => {
      const ts = _.sortBy(
        _.filter(daySlots, {
          employee_id: emp.id,
        }),
        "timestamp"
      );
      const slotNum = ts.length;
      const firstSlot = ts[0];
      const lastSlot = ts[slotNum - 1];
      const hrs = _.sumBy(ts, "hours") || 0;
      const oHrs = _.sumBy(ts, "original_hours") || 0;
      const isModified = _.sumBy(ts, (t) => (t.is_modified ? 1 : 0));
      const thMet = hrs >= breakThreshold;
      const maxBreakHours = _.min([hrs, breakHours]) || 0;
      const hoursMinusBreak = thMet ? hrs - maxBreakHours : hrs;

      const slot = {
        in: firstSlot && firstSlot.clockedIn,
        out: lastSlot && lastSlot.clockedOut,
        oIn: firstSlot && firstSlot.origClockedIn,
        oOut: lastSlot && lastSlot.origClockedOut,
        inFullDate: firstSlot && firstSlot.clockedInFullDate,
        outFullDate: lastSlot && lastSlot.clockedOutFullDate,
        oInFullDate: firstSlot && firstSlot.origClockedInFullDate,
        oOutFullDate: lastSlot && lastSlot.origClockedOutFullDate,
        daysIn: firstSlot && firstSlot.dayDiffIn,
        daysOut: lastSlot && lastSlot.dayDiffOut,
        hoursIn: firstSlot && firstSlot.diffInHours,
        hoursOut: lastSlot && lastSlot.diffOutHours,
        active: lastSlot && lastSlot.active,
        abnormal: lastSlot && lastSlot.abnormal,
        name: emp.full_name,
        status: emp.status,
        id: emp.id,
        oHours: parseFloat(oHrs).toFixed(2),
        obHours: parseFloat(hrs).toFixed(2),
        diffHours: (parseFloat(oHrs) - parseFloat(hrs)).toFixed(2),
        hours: parseFloat(hoursMinusBreak).toFixed(2),
        modified: !!isModified,
        valid: !!firstSlot,
        clockStatus: {
          isLateOut: lastSlot && lastSlot.isLateOut,
          isEarlyOut: lastSlot && lastSlot.isEarlyOut,
          isLateIn: firstSlot && firstSlot.isLateIn,
          isEarlyIn: firstSlot && firstSlot.isEarlyIn,
          isLongShift:
            hrs &&
            longShiftLimit &&
            parseFloat(hrs) > parseFloat(longShiftLimit),
        },
      };
      if (slotNum) {
        exportSlots.push(
          ...(ts.map((el, index) => ({
            in: el.clockedIn,
            out: el.clockedOut,
            inFullDate: el.clockedInFullDate,
            outFullDate: el.clockedOutFullDate,
            hours: el.hours,
            name: emp.full_name,
            status: emp.status,
            id: emp.id,
            active: index === ts.length - 1 && el.active,
          })) || [])
        );
      } else {
        exportSlots.push(slot);
      }

      gotSlots.push(slot);
    });

    return {
      day: thisDay,
      ymd: startDay.format("YYYY-MM-DD"),
      prettyDay: startDay.format("ddd MM/DD"),
      ts: startTs,
      invalid:
        _.filter(gotSlots, {
          valid: true,
        }).length < 1,
      slots: gotSlots,
      exportSlots,
      rawslots: daySlots,
    };
  };

  const groupByEmployee = (tsData, tsPeriodTotal, employees) =>
    _.map(employees || [], (e) => ({
      id: e.id,
      name: e.full_name,
      active: e.status === "ACTIVE",
      periodTotal:
        _.find(tsPeriodTotal || [], (p) => p.id === e.id)?.value || 0,
      timesheet: _.orderBy(
        tsData.map((t) => ({
          day: t.day,
          prettyDay: t.prettyDay,
          ts: t.ts,
          slots: _.find(t.slots || [], (s) => s.id === e.id),
        })),
        ["ts"]
      ),
    }));

  const groupByEmployeeForExport = (tsData, tsPeriodTotal, employees) =>
    _.map(employees || [], (e) => ({
      id: e.id,
      name: e.full_name,
      active: e.status === "ACTIVE",
      periodTotal:
        _.find(tsPeriodTotal || [], (p) => p.id === e.id)?.value || 0,
      timesheet: _.orderBy(
        tsData.flatMap((t) =>
          t.exportSlots
            ?.filter((s) => s.id === e.id)
            .map((slot) => ({
              day: t.day,
              prettyDay: t.prettyDay,
              ts: t.ts,
              slots: slot,
            }))
        ),
        ["ts"]
      ),
    }));

  const getFormattedTimesheets = (start, end, timesheetsinput = null) => {
    let timesheets = [...(timesheetsinput || timesheet || [])];
    const tz = selectedBusinessDetail.info.timezone;
    const startDay = _.isString(start)
      ? moment(start, "YYYY-MM-DD")
      : moment(start);
    const endDay = _.isString(end) ? moment(end, "YYYY-MM-DD") : moment(end);
    let startDayTs = startDay.valueOf();
    const endDayTs = endDay.endOf("day").valueOf();
    const tsData = [];
    const tsGroup = {};

    const employees = _.filter(
      employeeData || [],
      (e) => e.category !== "resource"
    );
    _.each(timesheets, (ts) => {
      updateTimesheet(ts, tz);
    });
    // const rawTimesheets = _.cloneDeep(timesheets);
    while (startDayTs < endDayTs) {
      tsData.push(
        getTimesheetSlot(employees, timesheets, startDay, startDayTs)
      );
      startDay.add(1, "day");
      startDayTs = startDay.valueOf();
    }

    timesheets = tsData;
    _.each(tsData, (d) => {
      _.each(d.slots, (sl) => {
        if (tsGroup[sl.id]) {
          tsGroup[sl.id] += +sl.hours || 0;
        } else {
          tsGroup[sl.id] = +sl.hours || 0;
        }
      });
    });
    const tsPeriodTotal = _.map(tsGroup, (g, i) => {
      const staff = _.find(employees, { id: +i });
      return {
        id: +i,
        name: staff.full_name,
        status: staff.status,
        value: g ? g.toFixed(2) : null,
      };
    });
    const groupedByEmployee = groupByEmployee(tsData, tsPeriodTotal, employees);

    const groupedByEmployeeForExport = groupByEmployeeForExport(
      tsData,
      tsPeriodTotal,
      employees
    );

    return {
      tsPeriodTotal,
      timesheets: _.orderBy(timesheets || [], ["ts"]),
      groupedByEmployee,
      groupedByEmployeeForExport,
    };
  };

  const getTimesheetsForDownload = async (startDate, endDate, ts = null) => {
    if (!ts) {
      const res = await callApi(
        `/protected/business/${selectedBusiness.id}/timesheets?start=${startDate}&end=${endDate}`
      );
      if (res && res.status === true && res.data) {
        const formatted = getFormattedTimesheets(startDate, endDate, res.data);
        return formatted?.groupedByEmployeeForExport || [];
      }
      toast.error((res && res.message) || "Failed to fetch staff timesheets!");
      return null;
    } 
      const formatted = getFormattedTimesheets(startDate, endDate, ts);
      return formatted?.groupedByEmployeeForExport || [];
    
  };

  const update = async ({ date, body, onSuccess }) => {
    setLoading(LOADING_TYPES.UPDATE_TIMESHEET);
    const res = await callApi(
      `/protected/business/${selectedBusiness?.id}/ts/updateall/${date}`,
      "POST",
      body
    );
    if (res && res.status) {
      toast.success("Successfully updated timesheet");
      if (onSuccess) onSuccess();
    } else {
      toast.error(res?.message || "Failed to update timesheet");
    }
    setLoading(null);
  };

  const clockOut = async ({ slotId, onSuccess }) => {
    setLoading(LOADING_TYPES.UPDATE_TIMESHEET);
    const res = await callApi(
      `/protected/business/${selectedBusiness?.id}/ts/${slotId}/clockout`,
      "POST"
    );
    if (res && res.status) {
      toast.success("Successfully clocked out");
      if (onSuccess) onSuccess();
      setLoading(null);
      return true;
    } else {
      toast.error(res?.message || "Failed to clock out");
      setLoading(null);
      return false;
    }
  }

  const getTimeSheetsActivity = async () => {
    setLoading(LOADING_TYPES.FETCH_ACTIVITY);
    const res = await callApi(
      `/protected/business/${selectedBusiness?.id}/activity/timesheets`
    );
    if (res && res.status) {
      setActivity(res.data);
    } else {
      toast.error(res?.message || "Failed to fetch activity");
    }
    setLoading(null);
  };

  return {
    update,
    clockOut,
    loadingUpdate: loading === LOADING_TYPES.UPDATE_TIMESHEET,
    timesheet,
    loadingFetch: loading === LOADING_TYPES.FETCH_TIMESHEET,
    getTimesheets,
    activity,
    loadingActivity: loading === LOADING_TYPES.FETCH_ACTIVITY,
    getTimeSheetsActivity,
    breakMinutes,
    breakThreshold,
    breakHours,
    breakSettings,
    getFormattedTimesheets,
    getTimesheetsForDownload,
    loadingSettingsUpdate: loading === LOADING_TYPES.UPDATE_SETTINGS,
    updateBusinessSettingsForTimesheet,
    settings,
    normalClockInTime: clockInLimit,
    normalClockOutTime: clockOutLimit,
    employeeData: _.filter(
      employeeData || [],
      (e) => e.category !== "resource"
    ),
  };
};

export default useStaffTimesheets;
